import { useEffect, useRef, useState } from "react";

function parseNumeric(v) {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const m = v.match(/-?\d[\d,]*(?:\.\d+)?/);
    if (m) return Number(m[0].replace(/,/g, ""));
  }
  return null;
}

function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(target ?? 0);
  const fromRef = useRef(target ?? 0);
  const rafRef = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = target ?? 0;
    if (from === to) return;
    const start = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setVal(from + (to - from) * eased);
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return val;
}

export default function StatCard({
  label,
  value,
  hint,
  accent = "gold",
  icon: Icon,
  delay = 0,
  trend,
  prefix = "",
  suffix = "",
  decimals,
  raw = false,
}) {
  // accent → visual tone
  const tones = {
    gold: {
      grad: "linear-gradient(135deg, rgba(34, 192, 115, 0.18) 0%, rgba(127, 29, 29, 0.04) 100%)",
      border: "rgba(34, 192, 115, 0.45)",
      glow: "rgba(34, 192, 115, 0.18)",
      iconColor: "#a7f3d0",
    },
    emerald: {
      grad: "linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(4,120,87,0.04) 100%)",
      border: "rgba(16,185,129,0.45)",
      glow: "rgba(16,185,129,0.18)",
      iconColor: "#6ee7b7",
    },
    amber: {
      grad: "linear-gradient(135deg, rgba(244,180,49,0.18) 0%, rgba(127, 29, 29, 0.04) 100%)",
      border: "rgba(244,180,49,0.45)",
      glow: "rgba(244,180,49,0.18)",
      iconColor: "#fca5a5",
    },
    blue: {
      grad: "linear-gradient(135deg, rgba(59,130,246,0.18) 0%, rgba(29,78,216,0.04) 100%)",
      border: "rgba(59,130,246,0.45)",
      glow: "rgba(59,130,246,0.18)",
      iconColor: "#93c5fd",
    },
    rose: {
      grad: "linear-gradient(135deg, rgba(229,75,89,0.18) 0%, rgba(159,18,57,0.04) 100%)",
      border: "rgba(229,75,89,0.45)",
      glow: "rgba(229,75,89,0.18)",
      iconColor: "#f87171",
    },
    slate: {
      grad: "linear-gradient(135deg, rgba(148,163,184,0.14) 0%, rgba(51,65,85,0.04) 100%)",
      border: "rgba(148,163,184,0.32)",
      glow: "rgba(148,163,184,0.14)",
      iconColor: "#cbd5e1",
    },
  };
  const t = tones[accent] || tones.gold;

  const numeric = raw ? null : parseNumeric(value);
  const animated = useCountUp(numeric ?? 0, 850);
  const showAnimated = !raw && numeric != null && numeric > 0;
  const decs =
    decimals != null
      ? decimals
      : typeof value === "string" && /[.,]\d/.test(value)
      ? 2
      : 0;
  const formatted = showAnimated
    ? `${prefix}${Number(animated).toLocaleString("en-US", {
        minimumFractionDigits: decs,
        maximumFractionDigits: decs,
      })}${suffix}`
    : value;

  return (
    <div
      className="p-glass p-card-hover p-fade-up"
      style={{
        padding: "clamp(14px, 3vw, 18px) clamp(14px, 3vw, 20px)",
        background: t.grad,
        border: `1px solid ${t.border}`,
        boxShadow: `0 18px 50px rgba(0,0,0,0.45), 0 0 0 1px ${t.glow} inset`,
        animationDelay: `${delay}ms`,
      }}
    >
      <div className="flex items-start justify-between mb-1.5 sm:mb-2 gap-2">
        <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-bold leading-tight" style={{ color: "rgba(245,241,230,0.6)" }}>
          {label}
        </div>
        {Icon && (
          <div
            className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${t.border}`,
              color: t.iconColor,
            }}
          >
            <Icon size={14} />
          </div>
        )}
      </div>
      <div className="font-black tracking-tight p-platinum-text leading-none" style={{ fontSize: "clamp(20px, 4.5vw, 28px)" }}>
        {formatted}
      </div>
      <div className="flex items-center gap-2 mt-1">
        {trend != null && (
          <span
            className={`text-[10.5px] font-bold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md`}
            style={{
              color: trend >= 0 ? "#6ee7b7" : "#f87171",
              background:
                trend >= 0 ? "rgba(16,185,129,0.12)" : "rgba(229,75,89,0.12)",
              border: `1px solid ${
                trend >= 0 ? "rgba(16,185,129,0.35)" : "rgba(229,75,89,0.35)"
              }`,
            }}
          >
            {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%
          </span>
        )}
        {hint && <div className="text-[10.5px] sm:text-[11px] text-white/45 truncate">{hint}</div>}
      </div>
    </div>
  );
}
